import { useParams } from "react-router-dom";
import { LaunchHeader } from "./components/launch-detail/LaunchHeader";
import { LaunchInfo } from "./components/launch-detail/LaunchInfo";
import { CodeList } from "./components/launch-detail/CodeList";
import { TemplateManager } from "./components/launch-detail/TemplateManager";
import { useLaunchDetail } from "./components/launch-detail/useLaunchDetail";
import { AddCodeDialog } from "./components/launch-detail/AddCodeDialog";
import { DeleteCodeDialog } from "./components/launch-detail/DeleteCodeDialog";
import { DeleteAllCodesDialog } from "./components/launch-detail/DeleteAllCodesDialog";
import { EditCodeDialog } from "./components/launch-detail/EditCodeDialog";
import { QrDialog } from "./components/launch-detail/QrDialog";
import EditLaunchDialog from "./components/EditLaunchDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

const LaunchDetail = () => {
  const { id } = useParams();
  const launchData = useLaunchDetail(id ? parseInt(id) : undefined);

  if (launchData.isLoadingLaunch || launchData.isLoadingCodes) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-48" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
        {/* Info Card Skeleton */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-32" />
              </div>
            ))}
          </CardContent>
        </Card>
        {/* Template Manager Skeleton */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
        {/* Codes List Skeleton */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <Skeleton className="h-6 w-24" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-28" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 border rounded">
                  <Skeleton className="h-4 w-24" />
                  <div className="flex gap-2">
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-8 w-8" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!launchData.launch) {
    return <div className="text-center py-8">Lanzamiento no encontrado</div>;
  }

  return (
    <div className="space-y-6">
      <LaunchHeader 
        launchId={launchData.launch.id} 
        onEditClick={() => launchData.setIsEditDialogOpen(true)} 
      />
      <LaunchInfo 
        launch={launchData.launch as any} 
        reportLifetimeDays={Math.ceil((launchData.launch.report_lifetime || 0) / (24 * 60 * 60))}
      />
      <TemplateManager launchId={launchData.launch.id.toString()} />
      <CodeList 
        codes={launchData.codes?.map(c => ({ ...c, title: c.code })) || []}
        onCopyCode={launchData.handleCopyCode}
        onCopyUrl={launchData.handleCopyUrl}
        onShowQr={launchData.handleShowQr}
        onEditCode={launchData.handleEditCode}
        onDeleteCode={(code) => {
          launchData.setCodeToDelete(code);
          launchData.setIsDeleteDialogOpen(true);
        }}
        onAddCodes={() => launchData.setIsAddDialogOpen(true)}
        onDeleteAll={() => launchData.setIsDeleteAllDialogOpen(true)}
        onCopyAll={launchData.handleCopyAllCodes}
      />

      <AddCodeDialog
        isOpen={launchData.isAddDialogOpen}
        onClose={() => launchData.setIsAddDialogOpen(false)}
        numCodesToAdd={launchData.numCodesToAdd}
        setNumCodesToAdd={launchData.setNumCodesToAdd}
        onAddCodes={launchData.handleAddCodes}
        isAddingCodes={launchData.isAddingCodes}
      />

      <DeleteCodeDialog
        isOpen={launchData.isDeleteDialogOpen}
        onOpenChange={launchData.setIsDeleteDialogOpen}
        codeToDelete={launchData.codeToDelete}
        onDelete={launchData.handleDeleteCode}
        isDeleting={launchData.isDeletingCode}
      />

      <DeleteAllCodesDialog
        isOpen={launchData.isDeleteAllDialogOpen}
        onOpenChange={launchData.setIsDeleteAllDialogOpen}
        onDeleteAll={launchData.handleDeleteAllCodes}
        isDeleting={launchData.isDeletingAllCodes}
      />

      <EditCodeDialog
        isOpen={launchData.isEditCodeDialogOpen}
        onOpenChange={launchData.setIsEditCodeDialogOpen}
        editingCode={{ code: launchData.editingCode.code, title: launchData.editingCode.code }}
        onEditCodeChange={launchData.setEditingCode}
        onSaveEdit={launchData.handleSaveEditCode}
        isEditing={launchData.isEditingCode}
      />

      <QrDialog 
        isOpen={launchData.isQrDialogOpen}
        onOpenChange={launchData.setIsQrDialogOpen}
        currentQrCode={launchData.currentQrCode}
        currentQrUrl={launchData.currentQrUrl}
        onDownload={launchData.handleDownloadQrImage}
      />

      <EditLaunchDialog
        isOpen={launchData.isEditDialogOpen}
        onClose={() => launchData.setIsEditDialogOpen(false)}
        launchId={launchData.launch?.id || 0}
      />
    </div>
  );
};

export default LaunchDetail;
